const API_BASE = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'tonrex_admin_token';
const VERIFIED_KEY = 'tonrex_admin_verified';

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

function friendlyApiError(text, status) {
  const t = String(text || '').trim();
  if (t.startsWith('<!DOCTYPE') || t.startsWith('<html') || t.includes('501 Not Implemented')) {
    return new ApiError(
      'Sunucu admin API\'yi desteklemiyor (501). Hostinger\'a build + data/ + api/ klasörünü yükleyin veya Node çalıştırın.',
      status || 501
    );
  }
  if (t.length > 200) {
    return new ApiError('Sunucu beklenmeyen yanıt döndü (HTML). API/PHP kurulumunu kontrol edin.', status);
  }
  return null;
}

async function parseResponse(res) {
  const text = await res.text();
  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      const htmlErr = friendlyApiError(text, res.status);
      if (htmlErr) throw htmlErr;
      body = { error: text.slice(0, 300) };
    }
  }
  if (!res.ok) {
    const htmlErr = friendlyApiError(text, res.status);
    if (htmlErr) throw htmlErr;
    throw new ApiError(body?.error || res.statusText, res.status);
  }
  return body;
}

export function getAdminToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(VERIFIED_KEY);
}

/** Oturum + kod doğrulama bayrağını temizle */
export function clearAdminSession() {
  clearAdminToken();
}

export function setAdminSession(token) {
  setAdminToken(token);
  sessionStorage.setItem(VERIFIED_KEY, '1');
}

export function isAdminSessionComplete() {
  return Boolean(getAdminToken() && sessionStorage.getItem(VERIFIED_KEY) === '1');
}

export async function fetchSiteData() {
  const res = await fetch(`${API_BASE}/api/site`, { cache: 'no-store' });
  return parseResponse(res);
}

function isValidSitePayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  if (data.error) return false;
  return Boolean(
    data.packages?.length ||
      data.homeContent ||
      data.aboutContent ||
      data.contactInfo ||
      data.seoSettings ||
      data.links ||
      data.notifications?.items?.length
  );
}

async function tryFetchSiteJson(url) {
  const res = await fetch(url, { cache: 'no-store' });
  const text = await res.text();
  if (!res.ok || !text) return null;
  try {
    const data = JSON.parse(text);
    return isValidSitePayload(data) ? data : null;
  } catch {
    return null;
  }
}

/** API → /data/site.json → parça parça JSON (Hostinger statik dahil) */
export async function fetchSiteDataWithFallback() {
  try {
    const api = await fetchSiteData();
    if (isValidSitePayload(api)) {
      return { data: api, source: 'api' };
    }
  } catch {
    /* HTML / 501 / API kapalı */
  }

  const bundled = await tryFetchSiteJson('/data/site.json');
  if (bundled) {
    return { data: bundled, source: 'static' };
  }

  const piecemeal = await loadSiteDataPiecemeal();
  if (isValidSitePayload(piecemeal)) {
    return { data: piecemeal, source: 'partial' };
  }

  return { data: piecemeal, source: 'none' };
}

async function loadSiteDataPiecemeal() {
  const pick = async (url) => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const [
    packagesRaw,
    links,
    homeContent,
    aboutContent,
    contactInfo,
    seoSettings,
    notifications
  ] = await Promise.all([
    pick('/data/packages.json'),
    pick('/data/download.json'),
    pick('/data/homeContent.json'),
    pick('/data/aboutContent.json'),
    pick('/data/contactInfo.json'),
    pick('/data/seoSettings.json'),
    pick('/data/notifications.json')
  ]);

  return {
    packages: normalizePackagesList(packagesRaw),
    links,
    homeContent,
    aboutContent,
    contactInfo,
    seoSettings,
    notifications
  };
}

/** API yanıtı veya build/data/packages.json */
export function normalizePackagesList(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.packages)) return raw.packages;
  return [];
}

export async function fetchPackagesBackup() {
  try {
    const res = await fetch('/data/packages.json', { cache: 'no-store' });
    if (!res.ok) return [];
    return normalizePackagesList(await res.json());
  } catch {
    return [];
  }
}

/**
 * Sırayla: admin API → /api/site → /data/packages.json
 * Statik hosting’de admin boş dönerken build içindeki JSON’u okur.
 */
export async function fetchPackagesWithFallback({ useAdmin = true } = {}) {
  if (useAdmin && getAdminToken()) {
    try {
      const admin = await adminGetPackages();
      const list = normalizePackagesList(admin);
      if (list.length) return { list, source: 'admin' };
    } catch {
      /* admin yok veya oturum geçersiz */
    }
  }

  try {
    const { data: site } = await fetchSiteDataWithFallback();
    const list = normalizePackagesList(site?.packages);
    if (list.length) return { list, source: 'api' };
  } catch {
    /* yedek */
  }

  const backup = await fetchPackagesBackup();
  if (backup.length) return { list: backup, source: 'static' };

  return { list: [], source: 'none' };
}

async function adminFetch(path, options = {}) {
  const token = getAdminToken();
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  return parseResponse(res);
}

export async function adminLogin(password, code) {
  const res = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, code: String(code || '').trim() })
  });
  const data = await parseResponse(res);
  setAdminSession(data.token);
  return data;
}

export async function adminLogout() {
  try {
    await adminFetch('/admin/logout', { method: 'POST' });
  } finally {
    clearAdminSession();
  }
}

export async function adminMe() {
  return adminFetch('/admin/me');
}

export async function adminGetStats() {
  return adminFetch('/admin/stats');
}

export async function adminInitialize() {
  return adminFetch('/admin/initialize', { method: 'POST' });
}

export async function adminGetPackages() {
  return adminFetch('/admin/packages');
}

export async function adminCreatePackage(pkg) {
  return adminFetch('/admin/packages', {
    method: 'POST',
    body: JSON.stringify(pkg)
  });
}

export async function adminUpdatePackage(id, updates) {
  return adminFetch(`/admin/packages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

export async function adminDeletePackage(id) {
  return adminFetch(`/admin/packages/${id}`, { method: 'DELETE' });
}

export async function adminGetLinks() {
  return adminFetch('/admin/links');
}

export async function adminSaveLinks(links) {
  return adminFetch('/admin/links', {
    method: 'PUT',
    body: JSON.stringify(links)
  });
}

export async function adminGetContent(docName) {
  return adminFetch(`/admin/content/${docName}`);
}

export async function adminSaveContent(docName, data) {
  return adminFetch(`/admin/content/${docName}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function adminUploadLogo(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.readAsDataURL(file);
  });
  const match = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
  if (!match) throw new Error('Geçersiz görsel');
  return adminFetch('/admin/upload/logo', {
    method: 'POST',
    body: JSON.stringify({ mimeType: match[1], imageBase64: match[2] })
  });
}

/** Admin kayıtlarından sonra ön yüzün veriyi yenilemesi */
export function notifySiteDataRefresh() {
  window.dispatchEvent(new CustomEvent('tonrex:site-refresh'));
}
